import React from 'react';
import { useTranslation } from 'react-i18next';
import { Form, type FormRule, InputNumber, Modal, Select } from 'antd';
import debounce from 'lodash.debounce';
import { AdditionalCostType } from 'types/additional-cost';
import { FormCurrencySelect } from 'components/common/form-currency-select/form-currency-select';
import styles from './additional-cost-modal.module.scss';
import type { IAdditionalCostFormFields, IAdditionalCostModalProps } from './types';

const validationRules: Record<keyof IAdditionalCostFormFields, FormRule[]> = {
  type: [{ required: true, enum: Object.values(AdditionalCostType) }],
  value: [{ required: true, type: 'number', min: 0 }],
  currency: [{ required: false, type: 'string' }],
};

export const AdditionalCostModal: React.FC<IAdditionalCostModalProps> = (props) => {
  const { isOpen, onClose, isLoading, initialValues, onFinish, availableTypes = AdditionalCostType } = props;
  const { t } = useTranslation('additional-cost');

  const [form] = Form.useForm();
  const handleOk = debounce(() => form.submit(), 200);

  const onFinishForm = async (values: IAdditionalCostFormFields) => {
    await onFinish(values);
    onClose();
    setTimeout(() => {
      form.resetFields();
    }, 200);
  };

  const handleClose = () => {
    form.resetFields();
    onClose();
  };

  const additionalCostOptions = Object.entries(availableTypes).map(([key, value]) => ({
    label: value,
    value: key,
  }));

  return (
    <Modal
      title={t('add_additional_cost')}
      open={isOpen}
      confirmLoading={isLoading}
      onCancel={handleClose}
      onOk={handleOk}>
      <Form
        form={form}
        name='order_add_additional_cost'
        layout='vertical'
        className={styles.form}
        initialValues={initialValues}
        onFinish={onFinishForm}>
        <Form.Item label={t('type')} name='type' rules={validationRules.type}>
          <Select placeholder={t('type')} size='middle' options={additionalCostOptions} />
        </Form.Item>
        <Form.Item label={t('value')} name='value' rules={validationRules.value}>
          <InputNumber
            placeholder={t('value')}
            size='middle'
            addonAfter={<FormCurrencySelect />}
            className={styles.valueInput}
          />
        </Form.Item>
      </Form>
    </Modal>
  );
};
